import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  User, Compass, BookOpen, Briefcase, Award, FileText, Target,
  ChevronRight, ChevronDown, ExternalLink, Sparkles, TrendingUp,
  GraduationCap, Brain, Heart, Zap, CheckCircle2, Clock, AlertCircle,
  Circle, BarChart3, Map, Layers, Star, ArrowRight, RefreshCw,
  ShieldCheck, Info
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Status helpers ──────────────────────────────────────────────────────────
const STATUS = {
  complete:       { label: 'Complete',        color: 'text-emerald-400', bg: 'bg-emerald-500/12 border-emerald-500/25', dot: 'bg-emerald-400', Icon: CheckCircle2 },
  in_progress:    { label: 'In Progress',     color: 'text-amber-400',   bg: 'bg-amber-500/12 border-amber-500/25',   dot: 'bg-amber-400',   Icon: Clock },
  revision_due:   { label: 'Revision Due',    color: 'text-orange-400',  bg: 'bg-orange-500/12 border-orange-500/25', dot: 'bg-orange-400',  Icon: AlertCircle },
  not_started:    { label: 'Not Started',     color: 'text-slate-300',   bg: 'bg-slate-700/20 border-slate-600/20',   dot: 'bg-slate-500',   Icon: Circle },
  ready_for_review:{ label: 'Ready for Review',color:'text-cyan-400',    bg: 'bg-cyan-500/12 border-cyan-500/25',     dot: 'bg-cyan-400',    Icon: Sparkles },
};

const sc = (s) => STATUS[s] || STATUS.not_started;

// ── Section definitions (universal FuturePath sections) ────────────────────
// NC CDP 9-section fallback for when the API hasn't populated sections yet
const NC_CDP_SECTIONS_FALLBACK = [
  { label: 'Student Self-Assessment', status: 'not_started', completion: 0 },
  { label: 'Individual Goals (Career & Educational)', status: 'not_started', completion: 0 },
  { label: 'Educational & Career Options Explored', status: 'not_started', completion: 0 },
  { label: 'Courses of Study / 4-Year Plan', status: 'not_started', completion: 0 },
  { label: 'Work-Based Learning Experiences', status: 'not_started', completion: 0 },
  { label: 'Accomplishments & Credentials', status: 'not_started', completion: 0 },
  { label: 'Resume/Personal Statement', status: 'not_started', completion: 0 },
  { label: 'Parent/Guardian Input & Acknowledgment', status: 'not_started', completion: 0 },
  { label: 'Student & Counselor Notes', status: 'not_started', completion: 0 },
];

const NC_CDP_REFS = [
  'NC G.S. § 115C-174.26(a)(1), Interests, skills, aptitudes, and learning styles',
  'NC G.S. § 115C-174.26(a)(2), Individual goals, career and educational',
  'NC G.S. § 115C-174.26(a)(3), Career clusters and pathways explored',
  'NC G.S. § 115C-174.26(a)(4), Courses of study, including four-year course plan',
  'NC G.S. § 115C-174.26(a)(5), Work-based learning experiences; NC WBL standards',
  'NC G.S. § 115C-174.26(a)(6), Certificates, credentials, accomplishments',
  'NC G.S. § 115C-174.26(a)(7), Resume and personal statement',
  'NC G.S. § 115C-174.26(b), Parent/guardian review and acknowledgment',
  'NC G.S. § 115C-174.26(c), Annual counselor review and update',
];

const FP_SECTIONS = [
  { key: 'who_i_am',         label: 'Who I Am',              Icon: Brain,        tagline: 'Your strengths, personality & interests' },
  { key: 'career_exploration',label: 'Where I May Be Going', Icon: Compass,      tagline: 'Career direction, CTE pathway & outlook' },
  { key: 'what_paths_require',label: 'What Those Paths Require', Icon: Map,      tagline: 'Education, training & credentials needed' },
  { key: 'academic_path',    label: 'My Academic Path',      Icon: BookOpen,     tagline: '4-year course plan & advanced coursework' },
  { key: 'experiences',      label: 'My Experiences',        Icon: Briefcase,    tagline: 'Work-based learning, activities & achievements' },
  { key: 'future_plan',      label: 'My Future Plan',        Icon: GraduationCap,tagline: 'Postsecondary direction & key milestones' },
  { key: 'portfolio',        label: 'My Portfolio',          Icon: Award,        tagline: 'Resume, credentials & evidence of readiness' },
];

// ── Completion bar ──────────────────────────────────────────────────────────
const CompletionBar = ({ pct, color = 'from-cyan-500 to-blue-500' }) => (
  <div className="h-1.5 w-full bg-slate-700/50 rounded-full overflow-hidden">
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: `${pct}%` }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className={`h-full rounded-full bg-gradient-to-r ${color}`}
    />
  </div>
);

// ── Status pill ─────────────────────────────────────────────────────────────
const StatusPill = ({ status }) => {
  const s = sc(status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${s.bg} ${s.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

// ── Section accordion ───────────────────────────────────────────────────────
const SectionCard = ({ section, cdpSection, stateLabel, stateRef, required, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const { Icon } = section;
  const status = cdpSection?.status || 'not_started';
  const pct = cdpSection?.completion || 0;
  // A section is only "Complete" when its evidence actually is, never claim it at 50%.
  const shownStatus = status === 'complete' && pct < 100 ? 'in_progress' : status;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden hover:border-cyan-400/25 transition-colors"
      data-testid={`fp-cdp-section-${section.key}`}
    >
      <button
        className="w-full flex items-center gap-5 p-5 text-left hover:bg-white/[0.03] transition-colors"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <div className="w-11 h-11 rounded-2xl bg-cyan-500/[0.08] flex items-center justify-center flex-shrink-0 border border-cyan-400/20">
          <Icon className="w-5 h-5 text-cyan-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="font-outfit text-white font-semibold text-base">{section.label}</span>
            {required && stateRef && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/20 font-medium">
                {stateRef}
              </span>
            )}
            {required && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/20 font-medium">
                Required
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <CompletionBar pct={pct} color={
              pct >= 80 ? 'from-emerald-500 to-teal-400' :
              pct >= 40 ? 'from-amber-500 to-yellow-400' :
                          'from-slate-500 to-slate-400'
            } />
            <span className="text-xs text-slate-300 shrink-0 tabular-nums">{pct}%</span>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <StatusPill status={shownStatus} />
          {open ? <ChevronDown className="w-4 h-4 text-slate-300" /> : <ChevronRight className="w-4 h-4 text-slate-300" />}
        </div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-4 border-t border-white/[0.06]">
              <p className="text-sm text-slate-300 mb-4">{section.tagline}</p>

              {/* Section-specific content */}
              <SectionContent sectionKey={section.key} data={cdpSection?.data} />

              {/* Action link */}
              {cdpSection?.action_path && (
                <Link
                  to={cdpSection.action_path}
                  className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-500/[0.08] px-4 py-2 text-xs font-medium text-cyan-300 hover:bg-cyan-500/[0.14] transition-colors"
                  data-testid={`cdp-action-${section.key}`}
                >
                  {cdpSection.action || 'Update this section'}
                  <ArrowRight className="w-3 h-3" />
                </Link>
              )}

              {/* State label hint */}
              {stateLabel && (
                <p className="mt-3 text-[11px] text-slate-300 flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" />
                  State: <span className="text-slate-300">{stateLabel}</span>
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ── Section-specific content renderers ─────────────────────────────────────
const SectionContent = ({ sectionKey, data }) => {
  if (!data) return <p className="text-sm text-slate-300 italic">Complete your FuturePath journey to populate this section.</p>;

  switch (sectionKey) {
    case 'who_i_am':
      return (
        <div className="space-y-3">
          {data.riasec_scores && (
            <div>
              <p className="text-xs text-slate-300 mb-1.5 uppercase tracking-wide">Personality Type</p>
              <p className="text-sm font-semibold text-white">{data.type_label || 'Explorer'}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {Object.entries(data.riasec_scores || {}).sort(([,a],[,b]) => b-a).slice(0,3).map(([k,v]) => (
                  <span key={k} className="text-[11px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">
                    {k}: {v}
                  </span>
                ))}
              </div>
            </div>
          )}
          {data.top_traits?.length > 0 && (
            <div>
              <p className="text-xs text-slate-300 mb-1.5 uppercase tracking-wide">Top Traits</p>
              <div className="flex flex-wrap gap-1.5">
                {data.top_traits.map((t, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-300 border border-violet-500/20">{t}</span>
                ))}
              </div>
            </div>
          )}
          {data.interests?.length > 0 && (
            <div>
              <p className="text-xs text-slate-300 mb-1.5 uppercase tracking-wide">Interest Areas</p>
              <div className="flex flex-wrap gap-1.5">
                {data.interests.map((t, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-300 border border-blue-500/20">{t}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      );

    case 'career_exploration':
      return (
        <div className="space-y-3">
          {data.career_goal && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-white/[0.04] border border-white/10">
              <Target className="w-4 h-4 text-cyan-400 shrink-0" />
              <div>
                <p className="text-xs text-slate-300">Target Career</p>
                <p className="text-white font-semibold text-sm">{data.career_goal}</p>
              </div>
            </div>
          )}
          {data.cte_cluster && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-white/[0.04] border border-white/10">
              <Layers className="w-4 h-4 text-violet-400 shrink-0" />
              <div>
                <p className="text-xs text-slate-300">CTE Pathway</p>
                <p className="text-white font-semibold text-sm">{data.cte_cluster}</p>
                {data.confidence && <p className="text-xs text-slate-300">AI confidence: {data.confidence}</p>}
              </div>
            </div>
          )}
          {data.alternatives?.length > 0 && (
            <div>
              <p className="text-xs text-slate-300 mb-1.5 uppercase tracking-wide">Other Paths Explored</p>
              <div className="flex flex-wrap gap-1.5">
                {data.alternatives.map((a, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-300 border border-slate-600/30">{a}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      );

    case 'what_paths_require':
      return (
        <div className="space-y-2">
          {data.education_level && (
            <p className="text-sm text-slate-300"><span className="text-slate-300">Education: </span>{data.education_level}</p>
          )}
          {data.typical_entry && (
            <p className="text-sm text-slate-300"><span className="text-slate-300">Typical Entry: </span>{data.typical_entry}</p>
          )}
          {data.relevant_credentials?.length > 0 && (
            <div>
              <p className="text-xs text-slate-300 mb-1.5 uppercase tracking-wide">Relevant Credentials</p>
              <div className="flex flex-wrap gap-1.5">
                {data.relevant_credentials.slice(0,4).map((c, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">{c}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      );

    case 'academic_path':
      const plan = data.four_year_plan || {};
      return (
        <div className="space-y-3">
          {Object.keys(plan).length > 0 ? (
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(plan).slice(0,4).map(([grade, courses]) => (
                <div key={grade} className="p-2.5 rounded-lg bg-slate-800/50 border border-slate-700/30">
                  <p className="text-[11px] text-slate-300 mb-1 font-semibold">Grade {grade}</p>
                  <div className="space-y-0.5">
                    {(courses || []).slice(0,3).map((c, i) => (
                      <p key={i} className="text-[11px] text-slate-300">{c}</p>
                    ))}
                    {courses?.length > 3 && <p className="text-[10px] text-slate-300">+{courses.length-3} more</p>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-300 italic">No course plan yet, build yours in the Course Planner.</p>
          )}
          {data.cte_courses?.length > 0 && (
            <div>
              <p className="text-xs text-slate-300 mb-1.5 uppercase tracking-wide">CTE Courses</p>
              <div className="flex flex-wrap gap-1.5">
                {data.cte_courses.map((c, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-300 border border-blue-500/20">{c}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      );

    case 'experiences':
      return (
        <div className="space-y-3">
          {data.wbl_hours > 0 && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.04] border border-white/10">
              <Briefcase className="w-4 h-4 text-amber-400 shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-slate-300">Work-Based Learning Hours</p>
                <div className="flex items-center gap-2 mt-1">
                  <CompletionBar pct={Math.min(100, (data.wbl_hours / (data.wbl_goal||40)) * 100)} color="from-amber-500 to-yellow-400" />
                  <span className="text-sm font-semibold text-white shrink-0">{data.wbl_hours}h</span>
                </div>
                <p className="text-[11px] text-slate-300 mt-0.5">Goal: {data.wbl_goal || 40} hours</p>
              </div>
            </div>
          )}
          {data.experiences?.length > 0 && (
            <div className="space-y-2">
              {data.experiences.slice(0,3).map((e, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                  <span className="text-slate-300">{e.employer}, {e.role} ({e.hours}h)</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );

    case 'future_plan':
      return (
        <div className="space-y-2">
          {data.postsecondary_intent && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-white/[0.04] border border-white/10">
              <GraduationCap className="w-4 h-4 text-blue-400 shrink-0" />
              <div>
                <p className="text-xs text-slate-300">Postsecondary Direction</p>
                <p className="text-white font-semibold text-sm capitalize">
                  {(data.postsecondary_intent || '').replace(/_/g, ' ')}
                </p>
              </div>
            </div>
          )}
          {data.saved_colleges?.length > 0 && (
            <div>
              <p className="text-xs text-slate-300 mb-1.5 uppercase tracking-wide">Colleges Saved</p>
              <div className="flex flex-wrap gap-1.5">
                {data.saved_colleges.slice(0,4).map((c, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/20">
                    {c.name || c}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      );

    case 'portfolio':
      return (
        <div className="space-y-3">
          {data.has_resume && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-500/8 border border-emerald-500/20">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span className="text-sm text-emerald-300">Resume uploaded</span>
            </div>
          )}
          {data.credentials?.length > 0 && (
            <div>
              <p className="text-xs text-slate-300 mb-1.5 uppercase tracking-wide">Credentials Earned</p>
              <div className="flex flex-wrap gap-1.5">
                {data.credentials.slice(0,4).map((c, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/20">
                    {c.name || c}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      );

    default:
      return null;
  }
};

// ── Version history row ─────────────────────────────────────────────────────
const VersionRow = ({ version, idx }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-white/[0.06] last:border-0">
    <div className="w-8 h-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-bold text-slate-300 shrink-0">
      G{version.grade || '?'}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm text-white font-medium">
        {version.snapshot_reason === 'manual' ? 'Manual Snapshot' : version.snapshot_reason}
      </p>
      <p className="text-xs text-slate-300">{version.career_goal || 'No career goal recorded'}</p>
    </div>
    <span className="text-xs text-slate-300 shrink-0">
      {new Date(version.created_at).toLocaleDateString()}
    </span>
  </div>
);

// ── MAIN PAGE ───────────────────────────────────────────────────────────────
export default function FuturePathCDPPage() {
  const { user, getAuthHeader } = useAuth();
  const [cdp, setCdp] = useState(null);
  const [status, setStatus] = useState(null);
  const [framework, setFramework] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('plan');
  const [snapshotting, setSnapshotting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const headers = getAuthHeader();
        const [cdpRes, statusRes, fwRes, verRes] = await Promise.allSettled([
          axios.get(`${API}/nc/cdp`, headers),
          axios.get(`${API}/nc/cdp/status`, headers),
          axios.get(`${API}/compliance-framework/my-framework`, headers),
          axios.get(`${API}/nc/cdp/versions`, headers),
        ]);
        if (cdpRes.status === 'fulfilled') setCdp(cdpRes.value.data);
        if (statusRes.status === 'fulfilled') setStatus(statusRes.value.data);
        if (fwRes.status === 'fulfilled') setFramework(fwRes.value.data);
        if (verRes.status === 'fulfilled') setVersions(verRes.value.data.versions || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSnapshot = async () => {
    setSnapshotting(true);
    try {
      const headers = getAuthHeader();
      await axios.post(`${API}/nc/cdp/snapshot`, {}, headers);
      const verRes = await axios.get(`${API}/nc/cdp/versions`, headers);
      setVersions(verRes.data.versions || []);
    } finally {
      setSnapshotting(false);
    }
  };

  // Map FuturePath sections to state framework mappings
  const sectionMap = {};
  (framework?.section_mappings || []).forEach(m => { sectionMap[m.fp_section] = m; });

  // Map CDP API sections to FP section keys
  const cdpSectionsByFP = {};
  if (cdp?.sections) {
    cdp.sections.forEach(s => {
      // Map NC CDP section labels to our FP section keys
      const key =
        s.label === 'About You' ? 'who_i_am' :
        s.label === 'Goals' ? 'career_exploration' :
        s.label === 'Education' ? 'what_paths_require' :
        s.label === 'Current & Future Courses' ? 'academic_path' :
        s.label === 'Work-Based Learning' ? 'experiences' :
        s.label === 'Achievements' ? 'experiences' :
        s.label === 'Resume' ? 'portfolio' :
        s.label === 'Notes' ? 'portfolio' :
        s.label === 'Accommodations' ? 'portfolio' : null;
      if (key && !cdpSectionsByFP[key]) cdpSectionsByFP[key] = s;
    });
  }

  // Enrich section data for better rendering
  const enrichSection = (fpKey, cdpSection) => {
    if (!cdpSection) return null;
    const data = { ...(cdpSection?.data || {}) };

    if (fpKey === 'who_i_am' && cdp?.assessment) {
      data.riasec_scores = cdp.assessment?.riasec_scores;
      data.type_label = cdp.assessment?.type_label;
      data.top_traits = cdp.assessment?.top_traits;
      data.interests = cdp.assessment?.interests;
    }
    if (fpKey === 'career_exploration') {
      data.career_goal = cdp?.career_goal?.title || cdp?.goal_section?.career_goal;
      data.cte_cluster = cdp?.endorsement_section?.cluster_name;
      data.confidence = cdp?.endorsement_section?.cluster_confidence;
    }
    if (fpKey === 'what_paths_require') {
      data.education_level = cdp?.education_section?.education_level;
      data.typical_entry = cdp?.education_section?.typical_entry;
      data.relevant_credentials = cdp?.endorsement_section?.recommended_credentials;
    }
    if (fpKey === 'academic_path') {
      data.four_year_plan = cdp?.courses_section?.four_year_plan || {};
      data.cte_courses = cdp?.courses_section?.cte_courses || [];
    }
    if (fpKey === 'experiences') {
      data.wbl_hours = cdp?.wbl_section?.total_hours || 0;
      data.wbl_goal = cdp?.wbl_section?.goal_hours || status?.wbl_goal || 40;
      data.experiences = cdp?.wbl_section?.experiences || [];
    }
    if (fpKey === 'future_plan') {
      data.postsecondary_intent = cdp?.education_section?.postsecondary_intent || user?.postsecondary_intent;
      data.saved_colleges = cdp?.education_section?.saved_colleges || [];
    }
    if (fpKey === 'portfolio') {
      data.has_resume = cdp?.resume_section?.has_resume;
      data.credentials = cdp?.endorsement_section?.earned_credentials || [];
    }

    return { ...cdpSection, data };
  };

  const overallPct = cdp?.overall_completion || status?.evidence_score || 0;
  const rawStatus = status?.status || cdp?.overall_status || 'not_started';
  // Never show "Complete" against a plan that isn't at 100%.
  const cdpStatus = rawStatus === 'complete' && overallPct < 100
    ? (overallPct > 0 ? 'in_progress' : 'not_started')
    : rawStatus;
  const statePlanShort = framework?.plan_short || 'Career Development Plan';
  const stateCode = framework?.state_code;
  const currentMilestone = status?.current_milestone;

  if (loading) {
    return (
      <AppLayout>
        <div className="max-w-6xl mx-auto py-24 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 rounded-full border-2 border-cyan-500 border-t-transparent animate-spin mx-auto mb-4" />
            <p className="text-slate-300 text-sm">Loading your FuturePath plan…</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto space-y-8 pb-16" data-testid="futurepath-cdp-page">

        {/* ── Hero ────────────────────────────────────────────────────────── */}
        <motion.section initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#050e1a] px-7 py-8 md:px-10 md:py-10">
          <div aria-hidden className="pointer-events-none absolute -top-32 -right-20 h-80 w-80 rounded-full blur-3xl opacity-25"
            style={{ background: 'radial-gradient(circle, #22d3ee, transparent 68%)' }} />
          <div className="relative grid gap-8 lg:grid-cols-[1fr_300px] lg:items-center">
            <div className="min-w-0">
              <div className="flex items-center gap-2.5">
                <span className="text-[11px] uppercase tracking-[0.2em] text-cyan-300 font-semibold">My FuturePath Plan</span>
                {stateCode && stateCode !== 'GENERIC' && (
                  <span className="text-[10px] px-2.5 py-1 rounded-full bg-blue-500/15 text-blue-200 border border-blue-500/25 font-semibold">
                    {stateCode} CDP
                  </span>
                )}
              </div>
              <h1 className="font-outfit text-4xl sm:text-5xl font-semibold text-white tracking-tight mt-3">
                {user?.first_name ? `${user.first_name}'s plan for what's next` : "My plan for what's next"}
              </h1>
              <p className="text-base text-slate-300/90 mt-3 max-w-2xl leading-relaxed">
                A living career development plan, assembled from work you have already done, your assessment,
                your goals, your courses and your experience. Nothing to fill in twice.
              </p>
              <div className="flex flex-wrap items-center gap-3 mt-6">
                <StatusPill status={cdpStatus} />
                <button
                  onClick={handleSnapshot}
                  disabled={snapshotting}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.05] px-4 py-2 text-sm text-slate-200 hover:border-cyan-400/50 hover:bg-white/[0.09] transition-colors"
                  data-testid="cdp-snapshot-btn"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${snapshotting ? 'animate-spin' : ''}`} />
                  Save a version
                </button>
              </div>
            </div>

            {/* Completion ring */}
            <div className="flex flex-col items-center lg:items-end">
              <div className="relative" style={{ filter: 'drop-shadow(0 0 32px rgba(34,211,238,0.28))' }}>
                <svg width="188" height="188" viewBox="0 0 188 188">
                  <circle cx="94" cy="94" r="80" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="14" />
                  <motion.circle cx="94" cy="94" r="80" fill="none" stroke="#22d3ee" strokeWidth="14"
                    strokeLinecap="round" transform="rotate(-90 94 94)"
                    strokeDasharray={2 * Math.PI * 80}
                    initial={{ strokeDashoffset: 2 * Math.PI * 80 }}
                    animate={{ strokeDashoffset: 2 * Math.PI * 80 * (1 - overallPct / 100) }}
                    transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="font-outfit text-5xl font-bold text-white tracking-tight leading-none tabular-nums"
                    data-testid="cdp-completion">{overallPct}%</span>
                  <span className="mt-2 text-[10px] uppercase tracking-[0.2em] text-slate-300">complete</span>
                </div>
              </div>
            </div>
          </div>

          {currentMilestone && (
            <div className="relative mt-7 flex items-start gap-3 rounded-2xl border border-amber-500/25 bg-amber-500/[0.07] px-5 py-4">
              <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-amber-200 font-semibold">{currentMilestone.label}</p>
                <p className="text-xs text-amber-300/70 mt-0.5">{currentMilestone.description}</p>
              </div>
            </div>
          )}
        </motion.section>

        {/* ── Stats strip ─────────────────────────────────────────────────── */}
        {status && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Assessment', value: status.has_assessment ? 'Done' : 'Not started', done: status.has_assessment, icon: Brain },
              { label: 'Career goal', value: status.has_career_goal ? 'Set' : 'Not set', done: status.has_career_goal, icon: Target },
              { label: 'Course plan', value: status.has_course_plan ? 'Built' : 'Not built', done: status.has_course_plan, icon: BookOpen },
              { label: 'Work-based learning', value: `${Math.round(status.wbl_hours || 0)}/${status.wbl_goal || 40} hrs`, done: (status.wbl_hours||0) >= (status.wbl_goal||40), icon: Briefcase },
            ].map(({ label, value, done, icon: Icon }) => (
              <div key={label}
                className={`rounded-2xl border p-5 ${done
                  ? 'bg-emerald-500/[0.07] border-emerald-500/25'
                  : 'bg-white/[0.04] border-white/10'}`}
                data-testid={`cdp-stat-${label.replace(/\s+/g,'').toLowerCase()}`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-slate-300 font-semibold">{label}</p>
                  <Icon className={`w-4 h-4 ${done ? 'text-emerald-400' : 'text-slate-300'}`} />
                </div>
                <p className={`font-outfit text-2xl font-semibold mt-2 ${done ? 'text-emerald-400' : 'text-white'}`}>{value}</p>
              </div>
            ))}
          </div>
        )}

        {/* ── Tabs ────────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap gap-1.5 p-1.5 rounded-2xl bg-white/[0.04] border border-white/10 w-fit">
          {[
            { id: 'plan', label: 'My plan' },
            { id: 'compliance', label: `${statePlanShort} status` },
            { id: 'history', label: `Version history (${versions.length})` },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-5 py-2.5 text-sm font-medium rounded-xl transition-colors ${
                activeTab === t.id
                  ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-400/30'
                  : 'text-slate-300 hover:text-white hover:bg-white/[0.05] border border-transparent'
              }`}
              data-testid={`cdp-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Plan tab ────────────────────────────────────────────────────── */}
        <AnimatePresence mode="wait">
          {activeTab === 'plan' && (
            <motion.div key="plan"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="space-y-4"
            >
              {FP_SECTIONS.map((section, i) => {
                const mapping = sectionMap[section.key];
                const cdpSection = enrichSection(section.key, cdpSectionsByFP[section.key]);
                // Build a synthetic cdpSection for sections not returned by CDP API yet
                const fallback = {
                  status: user?.cdp_sections_complete >= (i + 1) ? 'complete' : 'not_started',
                  completion: user?.cdp_sections_complete >= (i + 1) ? 80 : 0,
                  data: {},
                  action_path: getActionPath(section.key),
                  action: `Build your ${section.label.toLowerCase()}`,
                };
                return (
                  <SectionCard
                    key={section.key}
                    section={section}
                    cdpSection={cdpSection || fallback}
                    stateLabel={mapping?.state_label}
                    stateRef={mapping?.state_ref}
                    required={mapping?.required}
                    defaultOpen={i === 0}
                  />
                );
              })}
            </motion.div>
          )}

          {/* ── Compliance tab ──────────────────────────────────────────────── */}
          {activeTab === 'compliance' && (
            <motion.div key="compliance"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="space-y-4"
            >
              {/* Framework header + overall bar */}
              <div className="p-4 rounded-2xl bg-white/[0.04] border border-white/10">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="w-4 h-4 text-blue-400" />
                  <h3 className="text-sm font-semibold text-white">{framework?.plan_name || 'Career Development Plan'}</h3>
                  {framework?.authority && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-300 border border-blue-500/20 ml-auto">
                      {framework.authority}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-300 mb-3">{framework?.plan_name ? `${framework.state_code} G.S. § 115C-174.26, Career Development Plan requirement` : 'State-defined career development requirement'}</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 rounded-full bg-slate-700/50 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all"
                      style={{ width: `${overallPct}%` }}
                    />
                  </div>
                  <span className="text-sm font-bold text-white shrink-0">{overallPct}%</span>
                </div>
              </div>

              {/* 9-section required checklist */}
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
                <div className="px-4 py-3 border-b border-white/[0.07] flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-300">Required CDP Sections</p>
                  <span className="text-xs text-slate-300">
                    {cdp?.sections?.filter(s => s.status === 'complete').length || 0} / {cdp?.sections?.length || 9} complete
                  </span>
                </div>
                <div className="divide-y divide-white/[0.05]">
                  {(cdp?.sections || NC_CDP_SECTIONS_FALLBACK).map((sec, i) => {
                    const done = sec.status === 'complete';
                    const inProg = sec.status === 'in_progress';
                    return (
                      <div key={i} className={`flex items-center gap-3 px-4 py-3 ${done ? 'bg-emerald-500/3' : ''}`}>
                        <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold border"
                          style={{
                            background: done ? 'rgba(16,185,129,0.12)' : inProg ? 'rgba(245,158,11,0.10)' : 'rgba(255,255,255,0.04)',
                            borderColor: done ? 'rgba(16,185,129,0.30)' : inProg ? 'rgba(245,158,11,0.25)' : 'rgba(255,255,255,0.08)',
                            color: done ? '#34d399' : inProg ? '#fbbf24' : '#C6D4E8',
                          }}>
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white font-medium">{sec.label}</p>
                          <p className="text-[10px] text-slate-300 truncate">{NC_CDP_REFS[i] || 'Required by state plan'}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="hidden sm:flex w-16 h-1 rounded-full bg-slate-700/50 overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-500" style={{ width: `${sec.completion || 0}%` }} />
                          </div>
                          <span className="text-[10px] text-slate-300 w-8 text-right">{sec.completion || 0}%</span>
                          <StatusPill status={sec.status === 'complete' && (sec.completion || 0) < 100 ? 'in_progress' : (sec.status || 'not_started')} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Grade milestones */}
              {(framework?.grade_milestones || []).length > 0 && (
                <div className="p-4 rounded-2xl bg-white/[0.04] border border-white/10">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4 text-amber-400" />
                    Grade Milestones <span className="text-[11px] text-slate-300 font-normal ml-1">per NC G.S. § 115C-174.26</span>
                  </h3>
                  <div className="space-y-3">
                    {(framework.grade_milestones || []).map((m, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                          parseInt(user?.grade) >= m.grade
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            : 'bg-slate-800 text-slate-300 border border-slate-700'
                        }`}>
                          {m.grade}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-white">{m.label}</p>
                          <p className="text-xs text-slate-300">{m.description}</p>
                          <span className={`text-[10px] mt-0.5 inline-block ${
                            m.source === 'Verified NC Statute' ? 'text-emerald-400' : 'text-slate-300'
                          }`}>
                            {m.source}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Privacy link */}
              <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-500/6 border border-blue-500/15 cursor-pointer hover:bg-blue-500/10 transition-colors"
                onClick={() => window.open('/admin/privacy-compliance', '_blank')}
              >
                <ShieldCheck className="w-4 h-4 text-blue-400 shrink-0" />
                <p className="text-xs text-blue-300">View FERPA Audit Log & Data Privacy Agreement →</p>
              </div>
            </motion.div>
          )}

          {/* ── History tab ─────────────────────────────────────────────────── */}
          {activeTab === 'history' && (
            <motion.div key="history"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            >
              <div className="p-4 rounded-2xl bg-white/[0.04] border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-white">Plan Version History</h3>
                  <button
                    onClick={handleSnapshot}
                    disabled={snapshotting}
                    className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 transition-colors"
                  >
                    <RefreshCw className={`w-3 h-3 ${snapshotting ? 'animate-spin' : ''}`} />
                    Save Now
                  </button>
                </div>
                {versions.length === 0 ? (
                  <div className="text-center py-8">
                    <Layers className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-300 text-sm">No snapshots yet.</p>
                    <p className="text-slate-300 text-xs mt-1">
                      Snapshots save automatically at grade milestones, or hit "Save Version" any time.
                    </p>
                  </div>
                ) : (
                  <div>
                    {versions.map((v, i) => <VersionRow key={i} version={v} idx={i} />)}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </AppLayout>
  );
}

function getActionPath(fpKey) {
  const paths = {
    who_i_am: '/app/assessment',
    career_exploration: '/app/careers',
    what_paths_require: '/app/nc-cte',
    academic_path: '/app/course-planner',
    experiences: '/app/nc-wbl',
    future_plan: '/app/college-intel',
    portfolio: '/app/credentials',
  };
  return paths[fpKey] || '/app/dashboard';
}
